import { errorResponse } from "../api-interface";
import apiService from "../api-service";

// ============================================================================
// Functions from fileUploadApi.ts
// ============================================================================

// Types
export interface UploadResponse {
  fileId?: string;
  fileName?: string;
  fileUrl?: string;
  token?: string;
}

export interface UploadConfig {
  workGroupId: string;
  clientId: string;
  origin: string;
}

export interface KeyMetricsResponse {
  total_files_analysed: number;
  average_compliance_score: number;
  compliant_files: number;
  total_issues_found: number;
}

export interface CheckWiseCount {
  check_name: string;
  pass: number;
  fail: number;
  pass_rate: number;
}

export interface FindingsSummaryResponse {
  total_checks: number;
  pass_rate: number;
  passed: number;
  failed: number;
  check_wise_count: CheckWiseCount[];
}

export interface FindingsSummaryParams {
  brand?: string[];
  channel?: string[];
  date_range?: string;
}

const USE_MOCK = false;

/**
 * Get upload configuration
 */
const getUploadConfig = (): UploadConfig => {
  return {
    workGroupId:
      import.meta.env.VITE_WORK_GROUP_ID ||
      "9ff74475-b302-4ab5-bec5-8c91c362fa1c",
    clientId:
      import.meta.env.VITE_FILE_CLIENT_ID ||
      "46f836d6-dc07-4e87-94d8-fde07087ebd4",
    origin: import.meta.env.VITE_ORIGIN || "contentqa_Input",
  };
};

/**
 * Mock file upload for development
 */
const mockUploadBlob = async (file: File): Promise<UploadResponse> => {
  await new Promise((resolve) => setTimeout(resolve, 1000));
  return {
    fileId: `mock-file-${Date.now()}`,
    fileName: file.name,
    fileUrl: URL.createObjectURL(file),
  };
};

/**
 * Upload binary content to the platform file store
 * @param file - File object to upload
 * @param token - Authentication token
 * @returns Parsed JSON response from the platform API
 */
export const uploadBlob = async (
  file: File,
  token: string,
): Promise<UploadResponse> => {
  if (USE_MOCK) {
    return mockUploadBlob(file);
  }
  if (!file) {
    throw new Error("File must not be empty");
  }

  if (!file.name || !file.name.trim()) {
    throw new Error("File name must not be blank");
  }

  const config = getUploadConfig();

  const formData = new FormData();
  formData.append("file", file, file.name);
  formData.append("WorkGroupId", config.workGroupId);
  formData.append("ClientId", config.clientId);
  formData.append("Origin", config.origin);

  try {
    const response: any = await apiService.post({
      url: "filestorage/upload",
      data: formData,
      baseUrlKey: "platformServiceUrl",
      accessToken: token,
    });

    if (response?.data) {
      return response.data;
    } else {
      throw new Error("Upload failed: network connection issue");
    }
  } catch (error: any) {
    console.error("File upload error:", error);
    throw error;
  }
};

/**
 * Create task without token API
 * @param payload - Task creation payload
 * @param token - Optional authentication token
 * @returns Task creation response
 */
export const createTaskNoTokenAPI = async (
  payload: any,
  token?: string,
): Promise<any> => {
  try {
    const response: any = await apiService.post({
      url: "tasks",
      data: payload,
      baseUrlKey: "apiUrl",
      accessToken: token,
      axiosConfig: {
        headers: {
          "x-api-key": import.meta.env.VITE_API_KEY || "bms-content-qa",
        },
      },
    });

    if (response?.data) {
      return response.data;
    } else {
      return errorResponse(`Creating task failed: network connection issue`);
    }
  } catch (error: any) {
    console.error("Task creation error:", error);
    return errorResponse(
      `Creating task failed: ${error?.message || String(error)}`,
    );
  }
};

/**
 * Get task status by task ID
 * @param taskId - The task ID returned from createTaskNoTokenAPI
 * @param token - Optional authentication token
 * @returns Task status response
 */
export const getTaskStatus = async (
  taskId: string,
  token?: string,
): Promise<any> => {
  try {
    const response: any = await apiService.get({
      url: `tasks/${taskId}/status`,
      baseUrlKey: "apiUrl",
      accessToken: token,
      axiosConfig: {
        headers: {
          "x-api-key": import.meta.env.VITE_API_KEY || "bms-content-qa",
        },
      },
    });

    if (response?.data) {
      return response.data;
    } else {
      return errorResponse(
        `Failed to get task status: network connection issue`,
      );
    }
  } catch (error: any) {
    console.error("Task status error:", error);
    return errorResponse(
      `Failed to get task status: ${error?.message || String(error)}`,
    );
  }
};

/**
 * Get task results by task ID
 * @param taskId - The task ID returned from createTaskNoTokenAPI
 * @param token - Optional authentication token
 * @returns Task results
 */
export const getTaskResults = async (
  taskId: string,
  token?: string,
): Promise<any> => {
  try {
    const response: any = await apiService.get({
      url: `tasks/${taskId}/results`,
      baseUrlKey: "apiUrl",
      accessToken: token,
      axiosConfig: {
        headers: {
          "x-api-key": import.meta.env.VITE_API_KEY || "bms-content-qa",
        },
      },
    });

    if (response?.data) {
      return response.data;
    } else {
      return errorResponse(
        `Failed to get task results: network connection issue`,
      );
    }
  } catch (error: any) {
    console.error("Task results error:", error);
    return errorResponse(
      `Failed to get task results: ${error?.message || String(error)}`,
    );
  }
};

/**
 * Get key metrics for dashboard
 * @param params - Optional filters for brand, channel, and date range
 * @param token - Optional authentication token
 * @returns Key metrics data
 */
export const getKeyMetrics = async (
  params?: FindingsSummaryParams,
  token?: string,
): Promise<KeyMetricsResponse> => {
  // Build query parameters
  const queryParams: any = {};
  if (params?.brand && params.brand.length > 0) {
    queryParams.brand =
      params.brand.length > 1 ? params.brand : params.brand[0];
  }
  if (params?.channel && params.channel.length > 0) {
    queryParams.channel =
      params.channel.length > 1 ? params.channel : params.channel[0];
  }
  if (params?.date_range) {
    queryParams.date_range = params.date_range;
  }

  try {
    const response: any = await apiService.get({
      url: "dashboard/key_metrics",
      params: queryParams,
      baseUrlKey: "apiUrl",
      accessToken: token,
      axiosConfig: {
        headers: {
          "x-api-key": import.meta.env.VITE_API_KEY || "bms-content-qa",
        },
      },
    });

    if (response?.data) {
      return response.data;
    } else {
      throw new Error("Failed to get key metrics: network connection issue");
    }
  } catch (error: any) {
    console.error("Key metrics error:", error);
    throw error;
  }
};

/**
 * Get findings summary for dashboard
 * @param params - Optional filters for brand and channel
 * @param token - Optional authentication token
 * @returns Findings summary data
 */
export const getFindingsSummary = async (
  params?: FindingsSummaryParams,
  token?: string,
): Promise<FindingsSummaryResponse> => {
  // Build query parameters
  const queryParams: any = {};
  if (params?.brand && params.brand.length > 0) {
    queryParams.brand =
      params.brand.length > 1 ? params.brand : params.brand[0];
  }
  if (params?.channel && params.channel.length > 0) {
    queryParams.channel =
      params.channel.length > 1 ? params.channel : params.channel[0];
  }
  if (params?.date_range) {
    queryParams.date_range = params.date_range;
  }

  try {
    const response: any = await apiService.get({
      url: "dashboard/findings_summary",
      params: queryParams,
      baseUrlKey: "apiUrl",
      accessToken: token,
      axiosConfig: {
        headers: {
          "x-api-key": import.meta.env.VITE_API_KEY || "bms-content-qa",
        },
      },
    });

    if (response?.data) {
      return response.data;
    } else {
      throw new Error(
        "Failed to get findings summary: network connection issue",
      );
    }
  } catch (error: any) {
    console.error("Findings summary error:", error);
    throw error;
  }
};
